// Dependencies
import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import truncate from 'lodash.truncate';
import parse from 'html-react-parser';
import { motion, AnimatePresence } from 'framer-motion';

// Components
import Container from '../../../container/container';
import Image from '../../../image/image';

// Hooks
import useSuccessStories from '../../../../hooks/useSuccessStories';

// Utilities
import slugify from '../../../../utilities/slugify';

// Images
import cupidBow from '../../../../img/icons/cupid-bow.svg';

// Styles
import StoriesStyles from './stories.styles';

export default function Stories() {
  const successStories = useSuccessStories();

  const [list, setList] = useState([...successStories.slice(0, 6)]);
  const [loadMore, setLoadMore] = useState(false);
  const [hasMore, setHasMore] = useState(successStories.length > 6);

  const handleLoadMore = () => {
    setLoadMore(true);
  };

  useEffect(() => {
    if (!loadMore || !hasMore) return;

    const currentLength = list.length;
    const isMore = currentLength < successStories.length;
    const nextResults = isMore
      ? successStories.slice(currentLength, currentLength + 6)
      : [];

    setList([...list, ...nextResults]);
    setLoadMore(false);
  }, [loadMore, hasMore, list, successStories]);

  useEffect(() => {
    const isMore = list.length < successStories.length;
    setHasMore(isMore);
  }, [list, successStories]);

  return (
    <StoriesStyles>
      <Container>
        <AnimatePresence>
          <ul className="success-stories">
            {list.map((successStory) => {
              const title = successStory?.title;
              const uri = successStory?.uri;

              const matchmakerType =
                successStory?.successStories?.matchmakertype;
              const matchmaker = { type: matchmakerType };

              if (matchmakerType === 'teamMember') {
                const teamMember = successStory?.successStories?.teamMember;

                matchmaker.name = teamMember?.title;
                matchmaker.jobTitle =
                  teamMember?.teamMembers?.jobTitle || 'Matchmaker';
                matchmaker.headshot = teamMember?.featuredImage?.node;
                matchmaker.bio =
                  teamMember?.teamMembers?.successStoriesBio ||
                  teamMember?.teamMembers?.bio;

                if (teamMember?.title) {
                  matchmaker.link = `/meet-our-experts/?team-member=${slugify(
                    teamMember?.title,
                  )}`;
                }
              } else {
                matchmaker.name =
                  successStory?.successStories?.matchmakerNameOverride;
                matchmaker.jobTitle =
                  successStory?.successStories?.matchmakerJobTitleOverride;
              }

              const story = successStory?.successStories?.story;
              const truncatedStory = truncate(story, {
                length: 175,
                separator: ' ',
                omission: '...',
              });

              const matchedBy = (
                <>
                  <div>
                    {matchmakerType === 'teamMember' && (
                      <Image
                        image={matchmaker?.headshot}
                        className="headshot"
                      />
                    )}

                    {matchmakerType === 'override' && (
                      <div className="headshot">
                        <img
                          src={cupidBow}
                          alt="Cupid bow and arrow"
                          width={22}
                          className="icon"
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="name">Matched by {matchmaker?.name}</h4>
                    <p className="job-title">{matchmaker?.jobTitle}</p>
                  </div>
                </>
              );

              return (
                <motion.li
                  key={title}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    type: 'spring',
                    mass: 0.35,
                    stiffness: 75,
                    duration: 0.4,
                    delay: 0.3,
                  }}
                  className="success-story"
                >
                  <h3>{title}</h3>

                  {matchmakerType === 'teamMember' ? (
                    <Link to={matchmaker?.link} className="matched-by">
                      {matchedBy}
                    </Link>
                  ) : (
                    <div className="matched-by">{matchedBy}</div>
                  )}

                  <div className="story">{parse(truncatedStory)}</div>

                  <Link to={uri} className="read-more">
                    Read More
                  </Link>
                </motion.li>
              );
            })}
          </ul>
        </AnimatePresence>

        {!!hasMore && (
          <div className="controls">
            <button
              type="button"
              onClick={handleLoadMore}
              className="btn dark load-more"
            >
              Load More
            </button>
          </div>
        )}
      </Container>
    </StoriesStyles>
  );
}
